import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  WooVariation,
  WooProductAttribute,
  wooCreateVariation,
  wooUpdateVariation,
  wooDeleteVariation,
} from "../services/woo";
import { showToast } from "../assets/util/showToast";

type VariationFormData = {
  attributes: Record<string, string>;
  regular_price: string;
  sale_price: string;
  manage_stock: boolean;
  stock_quantity: string;
  stock_status: "instock" | "outofstock" | "onbackorder";
  sku: string;
  status: "publish" | "private" | "draft";
};

type BulkAction =
  | "delete"
  | "publish"
  | "draft"
  | "private"
  | "instock"
  | "outofstock"
  | "onbackorder"
  | "enable_stock_management"
  | "disable_stock_management"
  | "bulk_price_update"
  | "copy_from_regular_price"
  | "clear_sale_prices"
  | "duplicate_variations";

interface ProductVariationsManagerProps {
  productId: number;
  variations: WooVariation[];
  onVariationsChange: (variations: WooVariation[]) => void;
  productAttributes: WooProductAttribute[];
  loading?: boolean;
  error?: string | null;
}

export default function ProductVariationsManager({
  productId,
  variations,
  onVariationsChange,
  productAttributes,
  loading = false,
  error = null,
}: ProductVariationsManagerProps) {
  const [selectedVariations, setSelectedVariations] = useState<Set<number>>(
    new Set()
  );
  const [newVariation, setNewVariation] = useState<VariationFormData>({
    attributes: {},
    regular_price: "",
    sale_price: "",
    manage_stock: false,
    stock_quantity: "",
    stock_status: "instock",
    sku: "",
    status: "publish",
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [creating, setCreating] = useState(false);

  // Filter and search state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [stockFilter, setStockFilter] = useState<string>("all");
  const [attributeFilters, setAttributeFilters] = useState<
    Record<string, string>
  >({});
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [showFilters, setShowFilters] = useState(false);

  // Bulk operation modal state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkModalType, setBulkModalType] = useState<BulkAction | null>(null);
  const [bulkSettings, setBulkSettings] = useState({
    priceAdjustment: "",
    adjustmentType: "fixed" as "fixed" | "percentage" | "override",
    stockQuantity: "",
    confirmText: "",
  });
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [showBulkDropdown, setShowBulkDropdown] = useState(false);

  // Ref for select all checkbox to handle indeterminate state
  const selectAllRef = useRef<HTMLInputElement>(null);

  // Handle keyboard events for dropdown
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showBulkDropdown) {
        setShowBulkDropdown(false);
      }
    };

    if (showBulkDropdown) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [showBulkDropdown]);

  const variationAttributes = useMemo(
    () => productAttributes.filter((attr) => attr.variation),
    [productAttributes]
  );

  const allAttributeCombinations = useMemo(() => {
    if (!variationAttributes.length) return [];

    const generateCombinations = (
      attrs: WooProductAttribute[]
    ): Record<string, string>[] => {
      if (attrs.length === 0) return [{}];

      const [first, ...rest] = attrs;
      const restCombinations = generateCombinations(rest);
      const combinations: Record<string, string>[] = [];

      for (const option of first.options || []) {
        for (const restComb of restCombinations) {
          combinations.push({
            [first.name]: option,
            ...restComb,
          });
        }
      }

      return combinations;
    };

    return generateCombinations(variationAttributes);
  }, [variationAttributes]);

  const existingCombinations = useMemo(() => {
    return new Set(
      variations.map(
        (v) =>
          v.attributes
            ?.map((attr: any) => `${attr.name}:${attr.option}`)
            .sort()
            .join("|") || ""
      )
    );
  }, [variations]);

  const availableCombinations = useMemo(() => {
    return allAttributeCombinations.filter((combo) => {
      const comboKey = Object.entries(combo)
        .map(([name, option]) => `${name}:${option}`)
        .sort()
        .join("|");
      return !existingCombinations.has(comboKey);
    });
  }, [allAttributeCombinations, existingCombinations]);

  // Filtered variations based on all active filters
  const filteredVariations = useMemo(() => {
    return variations.filter((variation) => {
      // Text search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const sku = variation.sku?.toLowerCase() || "";
        const attributesText =
          variation.attributes
            ?.map((attr) => `${attr.name}: ${attr.option}`)
            .join(" ")
            .toLowerCase() || "";

        if (
          !sku.includes(searchLower) &&
          !attributesText.includes(searchLower)
        ) {
          return false;
        }
      }

      // Status filter
      if (statusFilter !== "all" && variation.status !== statusFilter) {
        return false;
      }

      // Stock filter
      if (stockFilter !== "all") {
        if (stockFilter === "instock" && variation.stock_status !== "instock") {
          return false;
        }
        if (
          stockFilter === "outofstock" &&
          variation.stock_status !== "outofstock"
        ) {
          return false;
        }
        if (
          stockFilter === "onbackorder" &&
          variation.stock_status !== "onbackorder"
        ) {
          return false;
        }
        if (stockFilter === "managed" && !variation.manage_stock) {
          return false;
        }
        if (stockFilter === "not_managed" && variation.manage_stock) {
          return false;
        }
        if (stockFilter === "low_stock") {
          if (
            !variation.manage_stock ||
            !variation.stock_quantity ||
            variation.stock_quantity > 5
          ) {
            return false;
          }
        }
      }

      // Attribute filters
      for (const [attrName, attrValue] of Object.entries(attributeFilters)) {
        if (attrValue && attrValue !== "all") {
          const hasAttribute = variation.attributes?.some(
            (attr) => attr.name === attrName && attr.option === attrValue
          );
          if (!hasAttribute) {
            return false;
          }
        }
      }

      // Price range filter
      if (priceRange.min || priceRange.max) {
        const price = parseFloat(variation.regular_price || "0");
        if (priceRange.min && price < parseFloat(priceRange.min)) {
          return false;
        }
        if (priceRange.max && price > parseFloat(priceRange.max)) {
          return false;
        }
      }

      return true;
    });
  }, [
    variations,
    searchTerm,
    statusFilter,
    stockFilter,
    attributeFilters,
    priceRange,
  ]);

  // Handle indeterminate state for select all checkbox
  useEffect(() => {
    if (selectAllRef.current) {
      const selectedFilteredCount = filteredVariations.filter((v) =>
        selectedVariations.has(v.id)
      ).length;
      const allFiltered =
        filteredVariations.length > 0 &&
        selectedFilteredCount === filteredVariations.length;
      const someFiltered =
        selectedFilteredCount > 0 &&
        selectedFilteredCount < filteredVariations.length;

      selectAllRef.current.checked = allFiltered;
      selectAllRef.current.indeterminate = someFiltered;
    }
  }, [filteredVariations, selectedVariations]);

  const handleSelectAll = useCallback(() => {
    const filteredIds = new Set(filteredVariations.map((v) => v.id));
    const allFilteredSelected = filteredVariations.every((v) =>
      selectedVariations.has(v.id)
    );

    if (allFilteredSelected && filteredVariations.length > 0) {
      // Deselect all filtered variations
      setSelectedVariations((prev) => {
        const newSet = new Set(prev);
        filteredIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all filtered variations
      setSelectedVariations((prev) => {
        const newSet = new Set(prev);
        filteredIds.forEach((id) => newSet.add(id));
        return newSet;
      });
    }
  }, [filteredVariations, selectedVariations]);

  const handleSelectVariation = useCallback((id: number) => {
    setSelectedVariations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const handleCreateVariation = async (variationData: VariationFormData) => {
    try {
      setCreating(true);

      const attributesPayload = Object.entries(variationData.attributes).map(
        ([name, option]) => ({
          name,
          option,
        })
      );

      const stockQty =
        variationData.stock_quantity.trim() === ""
          ? null
          : isNaN(Number(variationData.stock_quantity))
          ? null
          : Number(variationData.stock_quantity);

      const created = await wooCreateVariation(productId, {
        attributes: attributesPayload,
        regular_price: variationData.regular_price || "",
        sale_price: variationData.sale_price || "",
        sku: variationData.sku || null,
        manage_stock: variationData.manage_stock,
        stock_quantity: variationData.manage_stock ? stockQty : null,
        stock_status: variationData.manage_stock
          ? undefined
          : variationData.stock_status,
        status: variationData.status,
      });

      onVariationsChange([created, ...variations]);
      showToast("Variation created successfully", "success");

      // Reset form
      setNewVariation({
        attributes: {},
        regular_price: "",
        sale_price: "",
        manage_stock: false,
        stock_quantity: "",
        stock_status: "instock",
        sku: "",
        status: "publish",
      });
    } catch (err: any) {
      showToast(err.message || "Failed to create variation", "error");
    } finally {
      setCreating(false);
    }
  };

  const handleCreateMultipleVariations = async () => {
    if (!availableCombinations.length) {
      showToast("No new combinations available", "info");
      return;
    }

    try {
      setCreating(true);
      const promises = availableCombinations.map((combo) =>
        wooCreateVariation(productId, {
          attributes: Object.entries(combo).map(([name, option]) => ({
            name,
            option,
          })),
          regular_price: newVariation.regular_price || "",
          sale_price: newVariation.sale_price || "",
          sku: "", // Let WooCommerce auto-generate SKUs
          manage_stock: newVariation.manage_stock,
          stock_quantity: newVariation.manage_stock
            ? newVariation.stock_quantity.trim() === ""
              ? null
              : Number(newVariation.stock_quantity)
            : null,
          stock_status: newVariation.manage_stock
            ? undefined
            : newVariation.stock_status,
          status: newVariation.status,
        })
      );

      const created = await Promise.all(promises);
      onVariationsChange([...created, ...variations]);
      showToast(`Created ${created.length} variations`, "success");
    } catch (err: any) {
      showToast(err.message || "Failed to create variations", "error");
    } finally {
      setCreating(false);
    }
  };

  // Filter management functions
  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setStockFilter("all");
    setAttributeFilters({});
    setPriceRange({ min: "", max: "" });
  };

  const handleAttributeFilterChange = (
    attributeName: string,
    value: string
  ) => {
    setAttributeFilters((prev) => ({
      ...prev,
      [attributeName]: value,
    }));
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (statusFilter !== "all") count++;
    if (stockFilter !== "all") count++;
    if (Object.values(attributeFilters).some((v) => v && v !== "all")) count++;
    if (priceRange.min || priceRange.max) count++;
    return count;
  };

  const handleUpdateVariation = async (
    id: number,
    updates: Partial<WooVariation>
  ) => {
    try {
      // Handle stock quantity conversion properly
      let stockQty: number | null = null;
      if (updates.stock_quantity !== undefined) {
        if (typeof updates.stock_quantity === "number") {
          stockQty = updates.stock_quantity;
        } else if (updates.stock_quantity === null) {
          stockQty = null;
        }
      }

      const updated = await wooUpdateVariation(productId, id, {
        ...updates,
        stock_quantity: updates.manage_stock ? stockQty : null,
        stock_status: updates.manage_stock ? undefined : updates.stock_status,
      });

      onVariationsChange(variations.map((v) => (v.id === id ? updated : v)));
      showToast("Variation updated", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to update variation", "error");
    }
  };

  const handleBulkAction = async (action: BulkAction) => {
    // Close dropdown first
    setShowBulkDropdown(false);

    if (selectedVariations.size === 0) {
      showToast("No variations selected", "info");
      return;
    }

    // Show modal for actions that need additional input
    if (
      ["bulk_price_update", "enable_stock_management", "delete"].includes(
        action
      )
    ) {
      setBulkModalType(action);
      setShowBulkModal(true);
      return;
    }

    // Execute simple bulk actions directly
    await executeBulkAction(action);
  };

  const executeBulkAction = async (action: BulkAction) => {
    try {
      setBulkUpdating(true);
      setBulkProgress({ current: 0, total: selectedVariations.size });

      const selectedIds = Array.from(selectedVariations);

      if (action === "delete") {
        // Delete with confirmation already handled in modal
        let current = 0;
        for (const id of selectedIds) {
          await wooDeleteVariation(productId, id);
          current++;
          setBulkProgress({ current, total: selectedIds.length });
        }
        onVariationsChange(
          variations.filter((v) => !selectedVariations.has(v.id))
        );
        showToast(`Deleted ${selectedVariations.size} variations`, "success");
      } else if (action === "duplicate_variations") {
        const duplicates = [];
        let current = 0;
        for (const id of selectedIds) {
          const original = variations.find((v) => v.id === id);
          if (original) {
            const duplicate = await wooCreateVariation(productId, {
              regular_price: original.regular_price,
              sale_price: original.sale_price,
              manage_stock: original.manage_stock,
              stock_quantity: original.stock_quantity,
              stock_status: original.stock_status,
              sku: original.sku ? `${original.sku}-copy` : "",
              status: "draft", // New duplicates start as draft
              attributes: original.attributes || [],
            });
            duplicates.push(duplicate);
          }
          current++;
          setBulkProgress({ current, total: selectedIds.length });
        }
        onVariationsChange([...variations, ...duplicates]);
        showToast(`Duplicated ${duplicates.length} variations`, "success");
      } else {
        // Handle other bulk update operations
        const updates: Partial<WooVariation> = {};

        if (["publish", "draft", "private"].includes(action)) {
          updates.status = action as "publish" | "draft" | "private";
        } else if (["instock", "outofstock", "onbackorder"].includes(action)) {
          updates.stock_status = action as
            | "instock"
            | "outofstock"
            | "onbackorder";
          updates.manage_stock = false; // Set to false so stock_status takes effect
        } else if (action === "disable_stock_management") {
          updates.manage_stock = false;
          updates.stock_status = "instock";
        } else if (action === "enable_stock_management") {
          updates.manage_stock = true;
          updates.stock_quantity = parseInt(bulkSettings.stockQuantity) || 0;
        } else if (action === "clear_sale_prices") {
          updates.sale_price = "";
        } else if (action === "copy_from_regular_price") {
          // This needs to be handled per-variation
        } else if (action === "bulk_price_update") {
          // This needs to be handled per-variation with custom logic
        }

        let current = 0;
        const updatedVariations: WooVariation[] = [];

        for (const id of selectedIds) {
          let variationUpdates = { ...updates };

          // Handle special price operations
          if (action === "copy_from_regular_price") {
            const variation = variations.find((v) => v.id === id);
            if (variation?.regular_price) {
              variationUpdates.sale_price = variation.regular_price;
            }
          } else if (action === "bulk_price_update") {
            const variation = variations.find((v) => v.id === id);
            if (
              variation?.regular_price ||
              bulkSettings.adjustmentType === "override"
            ) {
              const currentPrice =
                parseFloat(variation?.regular_price || "0") || 0;
              const adjustment = parseFloat(bulkSettings.priceAdjustment) || 0;
              let newPrice: number;

              if (bulkSettings.adjustmentType === "percentage") {
                newPrice = currentPrice * (1 + adjustment / 100);
              } else if (bulkSettings.adjustmentType === "override") {
                newPrice = adjustment;
              } else {
                newPrice = currentPrice + adjustment;
              }

              variationUpdates.regular_price = Math.max(0, newPrice).toFixed(2);
            }
          }

          const updated = await wooUpdateVariation(
            productId,
            id,
            variationUpdates
          );
          updatedVariations.push(updated);
          current++;
          setBulkProgress({ current, total: selectedIds.length });
        }

        const updatedMap = new Map(updatedVariations.map((v) => [v.id, v]));
        onVariationsChange(variations.map((v) => updatedMap.get(v.id) || v));
        showToast(`Updated ${selectedVariations.size} variations`, "success");
      }

      setSelectedVariations(new Set());
      setBulkProgress({ current: 0, total: 0 });
    } catch (err: any) {
      showToast(err.message || "Bulk operation failed", "error");
    } finally {
      setBulkUpdating(false);
      setShowBulkModal(false);
      setBulkModalType(null);
      setBulkSettings({
        priceAdjustment: "",
        adjustmentType: "fixed",
        stockQuantity: "",
        confirmText: "",
      });
    }
  };

  const handleDeleteVariation = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    // eslint-disable-next-line no-restricted-globals
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Are you sure you want to delete this variation?")) return;

    try {
      await wooDeleteVariation(productId, id);
      onVariationsChange(variations.filter((v) => v.id !== id));
      showToast("Variation deleted", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to delete variation", "error");
    }
  };

  const updateVariationLocal = useCallback(
    (id: number, updates: Partial<WooVariation>) => {
      onVariationsChange(
        variations.map((v) => (v.id === id ? { ...v, ...updates } : v))
      );
    },
    [variations, onVariationsChange]
  );

  const formatAttributesSummary = (variation: WooVariation) => {
    if (!variation.attributes?.length) return "—";
    return variation.attributes
      .map((attr: any) => `${attr.name || ""}: ${attr.option}`)
      .join(", ");
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading variations...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-3">
            <strong>Product Variations</strong>
            <span className="badge bg-secondary text-white">
              {filteredVariations.length} of {variations.length}
            </span>
            {selectedVariations.size > 0 && (
              <span className="badge bg-primary">
                {selectedVariations.size} selected
              </span>
            )}
          </div>

          <div className="d-flex gap-2 align-items-center">
            {/* Search Box */}
            <div className="position-relative">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search SKU or attributes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: "2.5rem", width: "250px" }}
              />
              <i
                className="fas fa-search position-absolute"
                style={{
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6c757d",
                }}
              ></i>
            </div>

            {/* Filter Toggle */}
            <button
              className={`btn d-flex align-items-center gap-2 btn-sm ${
                showFilters ? "btn-primary" : "btn-outline-secondary"
              }`}
              onClick={() => setShowFilters(!showFilters)}
              title="Toggle Filters"
            >
              <i className="fas fa-filter me-1"></i>
              Filters
              {getActiveFilterCount() > 0 && (
                <span className="badge bg-danger ms-1 text-white">
                  {getActiveFilterCount()}
                </span>
              )}
            </button>

            {/* Reset Filters */}
            {getActiveFilterCount() > 0 && (
              <button
                className="btn btn-sm btn-outline-warning"
                onClick={resetFilters}
                title="Reset all filters"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="border rounded p-3 mb-3 bg-light">
            <div className="row g-3">
              {/* Status Filter */}
              <div className="col-md-3">
                <label className="form-label fw-bold">Status</label>
                <select
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="publish">Published</option>
                  <option value="draft">Draft</option>
                  <option value="private">Private</option>
                </select>
              </div>

              {/* Stock Filter */}
              <div className="col-md-3">
                <label className="form-label fw-bold">Stock</label>
                <select
                  className="form-select form-select-sm"
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                >
                  <option value="all">All Stock</option>
                  <option value="instock">In Stock</option>
                  <option value="outofstock">Out of Stock</option>
                  <option value="onbackorder">On Backorder</option>
                  <option value="managed">Quantity Managed</option>
                  <option value="not_managed">Status Only</option>
                  <option value="low_stock">Low Stock (≤5)</option>
                </select>
              </div>

              {/* Price Range */}
              <div className="col-md-3">
                <label className="form-label fw-bold">Price Range</label>
                <div className="d-flex gap-1">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="Min"
                    value={priceRange.min}
                    onChange={(e) =>
                      setPriceRange((prev) => ({
                        ...prev,
                        min: e.target.value,
                      }))
                    }
                  />
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="Max"
                    value={priceRange.max}
                    onChange={(e) =>
                      setPriceRange((prev) => ({
                        ...prev,
                        max: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Attribute Filters */}
              {variationAttributes.length > 0 && (
                <div className="col-md-3">
                  <label className="form-label fw-bold">Attributes</label>
                  <div className="d-flex flex-column gap-1">
                    {variationAttributes.slice(0, 2).map((attr) => (
                      <select
                        key={attr.name}
                        className="form-select form-select-sm"
                        value={attributeFilters[attr.name] || "all"}
                        onChange={(e) =>
                          handleAttributeFilterChange(attr.name, e.target.value)
                        }
                      >
                        <option value="all">All {attr.name}</option>
                        {attr.options?.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            {filteredVariations.length !== variations.length && (
              <small className="text-muted">
                Showing {filteredVariations.length} of {variations.length}{" "}
                variations
              </small>
            )}
            {filteredVariations.length === 0 && variations.length > 0 && (
              <small className="text-warning">
                <i className="fas fa-exclamation-triangle me-1"></i>
                No variations match the current filters
              </small>
            )}
          </div>

          <div className="d-flex gap-2">
            {selectedVariations.size > 0 && (
              <div className="dropdown position-relative">
                <button
                  className="btn btn-outline-secondary btn-sm dropdown-toggle"
                  type="button"
                  disabled={bulkUpdating}
                  onClick={() => setShowBulkDropdown(!showBulkDropdown)}
                >
                  {bulkUpdating ? (
                    <>
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></div>
                      {bulkProgress.total > 0 &&
                        `${bulkProgress.current}/${bulkProgress.total}`}
                    </>
                  ) : (
                    <>Bulk Actions ({selectedVariations.size})</>
                  )}
                </button>
                {showBulkDropdown && (
                  <>
                    {/* Backdrop to close dropdown when clicking outside */}
                    <div
                      className="position-fixed top-0 start-0 w-100 h-100"
                      style={{ zIndex: 1040 }}
                      onClick={() => setShowBulkDropdown(false)}
                    ></div>
                    <ul
                      className="dropdown-menu dropdown-menu-end show position-absolute"
                      style={{
                        minWidth: "280px",
                        zIndex: 1050,
                        top: "100%",
                        right: "0",
                        marginTop: "2px",
                      }}
                    >
                      <li>
                        <h6 className="dropdown-header">📊 Status Actions</h6>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handleBulkAction("publish")}
                        >
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Publish Selected
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handleBulkAction("draft")}
                        >
                          <i className="fas fa-edit text-warning me-2"></i>Set
                          as Draft
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handleBulkAction("private")}
                        >
                          <i className="fas fa-lock text-secondary me-2"></i>Set
                          as Private
                        </button>
                      </li>

                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      <li>
                        <h6 className="dropdown-header">📦 Stock Actions</h6>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handleBulkAction("instock")}
                        >
                          <i className="fas fa-check text-success me-2"></i>Mark
                          In Stock
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handleBulkAction("outofstock")}
                        >
                          <i className="fas fa-times text-danger me-2"></i>Mark
                          Out of Stock
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handleBulkAction("onbackorder")}
                        >
                          <i className="fas fa-clock text-info me-2"></i>Set On
                          Backorder
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() =>
                            handleBulkAction("enable_stock_management")
                          }
                        >
                          <i className="fas fa-cogs text-primary me-2"></i>
                          Enable Stock Management
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() =>
                            handleBulkAction("disable_stock_management")
                          }
                        >
                          <i className="fas fa-toggle-off text-secondary me-2"></i>
                          Disable Stock Management
                        </button>
                      </li>

                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      <li>
                        <h6 className="dropdown-header">💰 Price Actions</h6>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handleBulkAction("bulk_price_update")}
                        >
                          <i className="fas fa-dollar-sign text-success me-2"></i>
                          Update Prices
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() =>
                            handleBulkAction("copy_from_regular_price")
                          }
                        >
                          <i className="fas fa-copy text-info me-2"></i>Copy
                          Regular → Sale Price
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handleBulkAction("clear_sale_prices")}
                        >
                          <i className="fas fa-eraser text-warning me-2"></i>
                          Clear Sale Prices
                        </button>
                      </li>

                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      <li>
                        <h6 className="dropdown-header">🔧 Other Actions</h6>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() =>
                            handleBulkAction("duplicate_variations")
                          }
                        >
                          <i className="fas fa-clone text-primary me-2"></i>
                          Duplicate Selected
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item text-danger"
                          onClick={() => handleBulkAction("delete")}
                        >
                          <i className="fas fa-trash me-2"></i>Delete Selected
                        </button>
                      </li>
                    </ul>
                  </>
                )}
              </div>
            )}

            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? "Cancel" : "+ Add Variation"}
            </button>
          </div>
        </div>
      </div>

      <div className="card-body">
        {error && (
          <div className="alert alert-danger">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Add Variation Form */}
        {showAddForm && (
          <div className="border rounded p-3 mb-4 bg-light">
            <h6 className="mb-3">Add New Variation</h6>

            {variationAttributes.length === 0 ? (
              <div className="alert alert-warning">
                No variation attributes found. Please add product attributes
                with "Used for variations" enabled first.
              </div>
            ) : (
              <>
                <div className="row g-3 mb-3">
                  {/* Attribute Selects */}
                  {variationAttributes.map((attr) => (
                    <div className="col-md-4" key={attr.name}>
                      <label className="form-label">{attr.name}</label>
                      <select
                        className="form-select"
                        value={newVariation.attributes[attr.name] || ""}
                        onChange={(e) =>
                          setNewVariation((prev) => ({
                            ...prev,
                            attributes: {
                              ...prev.attributes,
                              [attr.name]: e.target.value,
                            },
                          }))
                        }
                      >
                        <option value="">— Select {attr.name} —</option>
                        {attr.options?.map((option: string) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}

                  {/* Price Fields */}
                  <div className="col-md-3">
                    <label className="form-label">Regular Price</label>
                    <input
                      type="text"
                      className="form-control"
                      inputMode="decimal"
                      placeholder="e.g. 99.99"
                      value={newVariation.regular_price}
                      onChange={(e) =>
                        setNewVariation((prev) => ({
                          ...prev,
                          regular_price: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Sale Price</label>
                    <input
                      type="text"
                      className="form-control"
                      inputMode="decimal"
                      placeholder="e.g. 79.99"
                      value={newVariation.sale_price}
                      onChange={(e) =>
                        setNewVariation((prev) => ({
                          ...prev,
                          sale_price: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">SKU</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Optional"
                      value={newVariation.sku}
                      onChange={(e) =>
                        setNewVariation((prev) => ({
                          ...prev,
                          sku: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={newVariation.status}
                      onChange={(e) =>
                        setNewVariation((prev) => ({
                          ...prev,
                          status: e.target.value as VariationFormData["status"],
                        }))
                      }
                    >
                      <option value="publish">Published</option>
                      <option value="draft">Draft</option>
                      <option value="private">Private</option>
                    </select>
                  </div>

                  {/* Stock Management */}
                  <div className="col-md-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="manageStockNew"
                        checked={newVariation.manage_stock}
                        onChange={(e) =>
                          setNewVariation((prev) => ({
                            ...prev,
                            manage_stock: e.target.checked,
                          }))
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="manageStockNew"
                      >
                        Manage stock quantity
                      </label>
                    </div>
                  </div>

                  {newVariation.manage_stock ? (
                    <div className="col-md-4">
                      <label className="form-label">Stock Quantity</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="e.g. 100"
                        value={newVariation.stock_quantity}
                        onChange={(e) =>
                          setNewVariation((prev) => ({
                            ...prev,
                            stock_quantity: e.target.value,
                          }))
                        }
                      />
                    </div>
                  ) : (
                    <div className="col-md-4">
                      <label className="form-label">Stock Status</label>
                      <select
                        className="form-select"
                        value={newVariation.stock_status}
                        onChange={(e) =>
                          setNewVariation((prev) => ({
                            ...prev,
                            stock_status: e.target
                              .value as VariationFormData["stock_status"],
                          }))
                        }
                      >
                        <option value="instock">In Stock</option>
                        <option value="outofstock">Out of Stock</option>
                        <option value="onbackorder">On Backorder</option>
                      </select>
                    </div>
                  )}
                </div>

                <div className="d-flex gap-2 align-items-center">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleCreateVariation(newVariation)}
                    disabled={
                      creating || !Object.keys(newVariation.attributes).length
                    }
                  >
                    {creating ? "Creating..." : "Create Variation"}
                  </button>

                  {availableCombinations.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleCreateMultipleVariations}
                      disabled={creating}
                    >
                      {creating
                        ? "Creating..."
                        : `Create All (${availableCombinations.length})`}
                    </button>
                  )}

                  <span className="text-muted small">
                    {availableCombinations.length} combination(s) available
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Variations Table */}
        {variations.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 40 }}>
                    <div className="form-check">
                      <input
                        ref={selectAllRef}
                        className="form-check-input"
                        type="checkbox"
                        checked={
                          filteredVariations.length > 0 &&
                          filteredVariations.every((v) =>
                            selectedVariations.has(v.id)
                          )
                        }
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  <th style={{ minWidth: 200 }}>Attributes</th>
                  <th style={{ width: 120 }}>SKU</th>
                  <th style={{ width: 100 }}>Regular</th>
                  <th style={{ width: 100 }}>Sale</th>
                  <th style={{ width: 80 }} title="Stock management method">
                    Stock
                  </th>
                  <th
                    style={{ width: 130 }}
                    title="Stock quantity (when managed) or enable/disable"
                  >
                    Quantity
                  </th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVariations.map((variation) => (
                  <tr key={variation.id}>
                    <td>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedVariations.has(variation.id)}
                          onChange={() => handleSelectVariation(variation.id)}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="small">
                        {formatAttributesSummary(variation)}
                      </div>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={variation.sku || ""}
                        onChange={(e) =>
                          updateVariationLocal(variation.id, {
                            sku: e.target.value,
                          })
                        }
                        onBlur={() =>
                          handleUpdateVariation(variation.id, {
                            sku: variation.sku,
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        inputMode="decimal"
                        value={variation.regular_price || ""}
                        onChange={(e) =>
                          updateVariationLocal(variation.id, {
                            regular_price: e.target.value,
                          })
                        }
                        onBlur={() =>
                          handleUpdateVariation(variation.id, {
                            regular_price: variation.regular_price,
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        inputMode="decimal"
                        value={variation.sale_price || ""}
                        onChange={(e) =>
                          updateVariationLocal(variation.id, {
                            sale_price: e.target.value,
                          })
                        }
                        onBlur={() =>
                          handleUpdateVariation(variation.id, {
                            sale_price: variation.sale_price,
                          })
                        }
                      />
                    </td>
                    <td>
                      {variation.manage_stock ? (
                        <button
                          className="btn btn-info btn-sm"
                          onClick={() => {
                            if (
                              window.confirm(
                                "Disable quantity management? This will reset to stock status mode."
                              )
                            ) {
                              handleUpdateVariation(variation.id, {
                                manage_stock: false,
                                stock_quantity: null,
                                stock_status: "instock",
                              });
                            }
                          }}
                          title="Click to disable quantity management"
                        >
                          <i className="fa fa-cog me-1"></i>
                          Managed
                        </button>
                      ) : (
                        <select
                          className="form-select form-select-sm"
                          value={variation.stock_status || "instock"}
                          onChange={(e) =>
                            handleUpdateVariation(variation.id, {
                              stock_status: e.target
                                .value as typeof variation.stock_status,
                              manage_stock: false,
                            })
                          }
                        >
                          <option value="instock">In Stock</option>
                          <option value="outofstock">Out of Stock</option>
                          <option value="onbackorder">Backorder</option>
                        </select>
                      )}
                    </td>
                    <td>
                      {variation.manage_stock ? (
                        <div className="d-flex gap-1 align-items-center">
                          <input
                            type="number"
                            className="form-control form-control-sm flex-grow-1"
                            value={
                              typeof variation.stock_quantity === "number"
                                ? String(variation.stock_quantity)
                                : ""
                            }
                            onChange={(e) =>
                              updateVariationLocal(variation.id, {
                                stock_quantity: e.target.value
                                  ? Number(e.target.value)
                                  : null,
                              })
                            }
                            onBlur={() =>
                              handleUpdateVariation(variation.id, {
                                stock_quantity: variation.stock_quantity,
                                manage_stock: true,
                              })
                            }
                            placeholder="0"
                            min="0"
                          />
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Disable quantity management? This will reset to stock status mode."
                                )
                              ) {
                                handleUpdateVariation(variation.id, {
                                  manage_stock: false,
                                  stock_quantity: null,
                                  stock_status: "instock",
                                });
                              }
                            }}
                            title="Disable quantity management"
                            style={{ minWidth: "32px" }}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn btn-outline-primary btn-sm w-100"
                          onClick={() =>
                            handleUpdateVariation(variation.id, {
                              manage_stock: true,
                              stock_quantity: 0,
                            })
                          }
                          title="Enable quantity management"
                        >
                          <i className="fa fa-plus me-1"></i>
                          Enable
                        </button>
                      )}
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={variation.status || "publish"}
                        onChange={(e) =>
                          handleUpdateVariation(variation.id, {
                            status: e.target.value as typeof variation.status,
                          })
                        }
                      >
                        <option value="publish">Published</option>
                        <option value="draft">Draft</option>
                        <option value="private">Private</option>
                      </select>
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDeleteVariation(variation.id)}
                          title="Delete variation"
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="text-muted">
              <div className="fs-1 mb-2">📦</div>
              <h6>No variations yet</h6>
              <p className="small">
                {variationAttributes.length === 0
                  ? "Add product attributes with 'Used for variations' enabled to get started."
                  : "Click 'Add Variation' to create your first variation."}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Operations Modal */}
      {showBulkModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {bulkModalType === "delete" && "🗑️ Delete Variations"}
                  {bulkModalType === "bulk_price_update" && "💰 Update Prices"}
                  {bulkModalType === "enable_stock_management" &&
                    "📦 Enable Stock Management"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowBulkModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {bulkModalType === "delete" && (
                  <div>
                    <div className="alert alert-danger">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      You are about to permanently delete{" "}
                      <strong>{selectedVariations.size}</strong> variations.
                      This action cannot be undone.
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Type <strong>DELETE</strong> to confirm:
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={bulkSettings.confirmText}
                        onChange={(e) =>
                          setBulkSettings((prev) => ({
                            ...prev,
                            confirmText: e.target.value,
                          }))
                        }
                        placeholder="Type DELETE to confirm"
                      />
                    </div>
                  </div>
                )}

                {bulkModalType === "bulk_price_update" && (
                  <div>
                    <p>
                      Update prices for{" "}
                      <strong>{selectedVariations.size}</strong> selected
                      variations:
                    </p>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Adjustment Type:</label>
                        <select
                          className="form-select"
                          value={bulkSettings.adjustmentType}
                          onChange={(e) =>
                            setBulkSettings((prev) => ({
                              ...prev,
                              adjustmentType: e.target.value as
                                | "fixed"
                                | "percentage"
                                | "override",
                            }))
                          }
                        >
                          <option value="fixed">Fixed Amount ($)</option>
                          <option value="percentage">Percentage (%)</option>
                          <option value="override">Override Price ($)</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          {bulkSettings.adjustmentType === "fixed"
                            ? "Amount ($)"
                            : bulkSettings.adjustmentType === "percentage"
                            ? "Percentage (%)"
                            : "New Price ($)"}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          value={bulkSettings.priceAdjustment}
                          onChange={(e) =>
                            setBulkSettings((prev) => ({
                              ...prev,
                              priceAdjustment: e.target.value,
                            }))
                          }
                          placeholder={
                            bulkSettings.adjustmentType === "fixed"
                              ? "10.00"
                              : bulkSettings.adjustmentType === "percentage"
                              ? "15"
                              : "25.99"
                          }
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <small className="text-muted">
                        {bulkSettings.adjustmentType === "fixed"
                          ? "Positive values increase price, negative values decrease price"
                          : bulkSettings.adjustmentType === "percentage"
                          ? "Positive values increase price, negative values decrease price (e.g., 15 = +15%, -10 = -10%)"
                          : "Set all selected variations to this exact price (e.g., 10 sets all prices to $10.00)"}
                      </small>
                    </div>
                  </div>
                )}

                {bulkModalType === "enable_stock_management" && (
                  <div>
                    <p>
                      Enable stock management for{" "}
                      <strong>{selectedVariations.size}</strong> selected
                      variations:
                    </p>
                    <div className="mb-3">
                      <label className="form-label">
                        Default Stock Quantity:
                      </label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={bulkSettings.stockQuantity}
                        onChange={(e) =>
                          setBulkSettings((prev) => ({
                            ...prev,
                            stockQuantity: e.target.value,
                          }))
                        }
                        placeholder="e.g., 100"
                      />
                      <div className="form-text">
                        This will be the initial stock quantity for all selected
                        variations.
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowBulkModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${
                    bulkModalType === "delete" ? "btn-danger" : "btn-primary"
                  }`}
                  disabled={
                    bulkModalType === "delete"
                      ? bulkSettings.confirmText !== "DELETE"
                      : (bulkModalType === "bulk_price_update" &&
                          !bulkSettings.priceAdjustment) ||
                        (bulkModalType === "enable_stock_management" &&
                          !bulkSettings.stockQuantity)
                  }
                  onClick={() => executeBulkAction(bulkModalType!)}
                >
                  {bulkModalType === "delete" && "Delete Variations"}
                  {bulkModalType === "bulk_price_update" && "Update Prices"}
                  {bulkModalType === "enable_stock_management" &&
                    "Enable Stock Management"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
